/*
 * <delivery-estimate> — estimated delivery date on product pages.
 *
 * v1 is fully static: it reads a prebuilt lookup table shipped as a theme asset
 * (delivery-tat.json), so there is no backend, no API key in the browser and no
 * per-visitor call to Proship. Regenerate the table with
 * tat-master/build_committed_tat.py.
 *
 * Rules:
 *   Blitz pincode, not Delhi  -> "Express Delivery available"
 *   Blitz pincode in Delhi    -> a date, from the Blitz TAT
 *   Otherwise serviceable     -> a date, from Prozo's committed zone TAT
 *   Not serviceable / unknown -> honest message
 *
 * Design rule: strictly additive. Any failure hides the widget. It must never
 * block, delay or interfere with add-to-cart.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'gm_edd_pin';
  var PINCODE_RE = /^[1-9][0-9]{5}$/;
  var TIMEOUT_MS = 6000;
  var IST = 'Asia/Kolkata';

  var tablePromise = null; // shared across every instance on the page

  function loadTable(url) {
    if (tablePromise) return tablePromise;
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    tablePromise = fetch(url, { signal: controller.signal, credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('http ' + res.status);
        return res.json();
      })
      .then(function (data) {
        // Sets give O(1) membership on the plain arrays.
        data._notServiceable = new Set(data.notServiceable || []);
        var tiers = data.blitz || {};
        for (var tat in tiers) tiers[tat]._pins = new Set(tiers[tat].pins || []);
        return data;
      })
      .finally(function () { clearTimeout(timer); });
    return tablePromise;
  }

  function readStoredPin() {
    try {
      var v = window.localStorage.getItem(STORAGE_KEY);
      return v && PINCODE_RE.test(v) ? v : null;
    } catch (e) { return null; }
  }

  function storePin(pin) {
    try { window.localStorage.setItem(STORAGE_KEY, pin); } catch (e) {}
  }

  /** Today's date and hour in IST, regardless of the shopper's own timezone. */
  function nowIST() {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', hour12: false,
    }).formatToParts(new Date());
    var get = function (t) {
      return parts.find(function (p) { return p.type === t; }).value;
    };
    return {
      // UTC midnight stands in for an IST calendar date, so adding whole days is exact.
      date: new Date(Date.UTC(+get('year'), +get('month') - 1, +get('day'))),
      hour: parseInt(get('hour'), 10) % 24,
    };
  }

  /**
   * Delivery date = dispatch date + transit days.
   * Dispatch is today before the cutoff, otherwise tomorrow, and never a Sunday.
   */
  function deliveryDate(days, cutoffHour, closedSunday) {
    var now = nowIST();
    var d = new Date(now.date.getTime());
    if (now.hour >= cutoffHour) d.setUTCDate(d.getUTCDate() + 1);
    if (closedSunday) {
      while (d.getUTCDay() === 0) d.setUTCDate(d.getUTCDate() + 1);
    }
    d.setUTCDate(d.getUTCDate() + days);
    return d;
  }

  /** Merchant-editable strings land in innerHTML, so escape them. */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];

  function ordinal(n) {
    if (n % 100 >= 11 && n % 100 <= 13) return n + 'th';
    return n + ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  }

  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MON3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** -> 'Sat, 1 Nov' (the "Get it by" date, v3) */
  function formatShort(d) {
    return DAYS[d.getUTCDay()] + ', ' + d.getUTCDate() + ' ' + MON3[d.getUTCMonth()];
  }

  /** -> '25th August' */
  function formatDate(d) {
    return ordinal(d.getUTCDate()) + ' ' + MONTHS[d.getUTCMonth()];
  }

  /**
   * -> '25th to 26th August', collapsing the month when both dates share it,
   * and to a single date when the two bounds land on the same day.
   */
  function formatRange(a, b) {
    if (a.getTime() === b.getTime()) return formatDate(a);
    if (a.getUTCMonth() === b.getUTCMonth()) {
      return ordinal(a.getUTCDate()) + ' to ' + formatDate(b);
    }
    return formatDate(a) + ' to ' + formatDate(b);
  }

  /** Is this an actual pincode we serve? Exact check, not a prefix guess. */
  function isServiceable(table, pin) {
    var spec = table.serviceable && table.serviceable[pin.slice(0, 3)];
    if (!spec) return false;
    var n = parseInt(pin.slice(3), 10);
    var parts = spec.split(',');
    for (var i = 0; i < parts.length; i++) {
      var dash = parts[i].indexOf('-');
      if (dash === -1) {
        if (parseInt(parts[i], 10) === n) return true;
      } else if (n >= parseInt(parts[i].slice(0, dash), 10)
              && n <= parseInt(parts[i].slice(dash + 1), 10)) {
        return true;
      }
    }
    return false;
  }

  /**
   * v3 (29 Oct 2026): days are MEASURED, not promised - the 80th percentile of real "dispatch day -> delivered"
   * times for recent orders, per courier (B = Blitz pincodes, P = everything else) and pincode area (3-digit prefix,
   * then 2-digit, then national). Built by gimi-michi-bi/diagnostics/edd/build_actual_tat.py. A temporary buffer
   * (e.g. Diwali) is added until its end date.
   */
  function blitzTier(table, pin) {
    var tiers = table.blitz || {};
    for (var tat in tiers) {
      if (tiers[tat]._pins && tiers[tat]._pins.has(pin)) return tiers[tat];
    }
    return null;
  }

  function actualDays(table, pin, isBlitz) {
    var a = table.actual && table.actual[isBlitz ? 'B' : 'P'];
    if (!a) return null;
    var d = a[pin.slice(0, 3)];
    if (d == null) d = a[pin.slice(0, 2)];
    if (d == null) d = a['*'];
    if (d == null) return null;
    var buf = table.buffer;
    if (buf && buf.days && buf.until) {
      var today = nowIST().date.toISOString().slice(0, 10);
      if (today <= buf.until) d += buf.days;
    }
    return d;
  }

  function lookup(table, pin) {
    if (table.actual) {
      var tier = blitzTier(table, pin);
      if (!tier) {
        if (table._notServiceable.has(pin)) return { kind: 'unserviceable' };
        if (!isServiceable(table, pin)) return { kind: 'invalid' };
      }
      var days = actualDays(table, pin, !!tier);
      if (days != null) return { kind: 'by', days: days, express: !!(tier && tier.express) };
    }
    // Blitz first: it overrides Prozo wherever it reaches.
    var tiers = table.blitz || {};
    for (var tat in tiers) {
      if (tiers[tat]._pins && tiers[tat]._pins.has(pin)) {
        return {
          kind: 'range',
          minDays: tiers[tat].minDays,
          maxDays: tiers[tat].maxDays,
          express: !!tiers[tat].express,
        };
      }
    }
    if (table._notServiceable.has(pin)) return { kind: 'unserviceable' };
    // Exact membership BEFORE reading the prefix table, otherwise any 6 digits
    // under a known prefix would resolve to a date. 222222 did exactly that.
    if (!isServiceable(table, pin)) return { kind: 'invalid' };
    var days = (table.pinDays && table.pinDays[pin]);
    if (days == null && table.prefixDays) days = table.prefixDays[pin.slice(0, 3)];
    if (days != null) return { kind: 'date', days: days };
    return { kind: 'invalid' };
  }

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.tableUrl = this.dataset.table;
      this.cutoffHour = parseInt(this.dataset.cutoffHour, 10);
      if (!(this.cutoffHour >= 0 && this.cutoffHour <= 23)) this.cutoffHour = 14;
      this.closedSunday = this.dataset.closedSunday !== 'false';
      this.expressMessage = this.dataset.expressMessage || 'Express Delivery available';
      this.shippingNote = this.dataset.shippingNote || 'Free Shipping Above ₹249';

      this.form = this.querySelector('.gm-edd__form');
      this.input = this.querySelector('.gm-edd__input');
      this.submit = this.querySelector('.gm-edd__submit');
      this.result = this.querySelector('.gm-edd__result');

      if (!this.tableUrl || !this.form || !this.input || !this.result) {
        this.setAttribute('hidden', '');
        return;
      }

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.input.addEventListener('input', function () {
        var cleaned = this.value.replace(/\D/g, '').slice(0, 6);
        if (cleaned !== this.value) this.value = cleaned;
      });

      var saved = readStoredPin();
      if (saved) {
        this.input.value = saved;
        this.check(saved);
      }
    }

    onSubmit(event) {
      event.preventDefault();
      var pin = (this.input.value || '').trim();
      if (!PINCODE_RE.test(pin)) {
        this.render('<span class="gm-edd__result--warn">'
          + 'Enter a valid 6-digit pincode.</span>');
        this.input.focus();
        return;
      }
      storePin(pin);
      this.check(pin);
    }

    setBusy(busy) {
      // 'error' is terminal: fail() hides the widget, and a finally-block must
      // not resurrect it by resetting to 'ready'.
      if (this.dataset.state !== 'error') {
        this.dataset.state = busy ? 'loading' : 'ready';
      }
      if (this.submit) this.submit.disabled = busy;
    }

    render(html) { this.result.innerHTML = html; }

    fail() { this.dataset.state = 'error'; }

    check(pin) {
      var self = this;
      this.setBusy(true);
      this.render('Checking…');
      loadTable(this.tableUrl)
        .then(function (table) { self.show(lookup(table, pin), pin); })
        .catch(function () { self.fail(); })
        .finally(function () { self.setBusy(false); });
    }

    show(res, pin) {
      var change = '<button type="button" class="gm-edd__change" '
        + 'data-gm-edd-change>Change</button>';

      // The free-shipping note lives on the ribbon at the top edge (rendered in
      // Liquid), so it is deliberately not repeated here.
      var meta = '<span class="gm-edd__meta">'
        + '<span class="gm-edd__to">Delivering to ' + esc(pin) + '</span>'
        + '</span>';

      if (res.kind === 'by') {
        var by = formatShort(deliveryDate(res.days, this.cutoffHour, this.closedSunday));
        this.render((res.express ? '<span class="gm-edd__date gm-edd__date--express">⚡ '
          + esc(this.expressMessage) + '</span>' : '')
          + '<span class="gm-edd__when">Get it by <strong class="gm-edd__date">' + by + '</strong></span>'
          + change + meta);
      } else if (res.kind === 'range') {
        var from = deliveryDate(res.minDays, this.cutoffHour, this.closedSunday);
        var to = deliveryDate(res.maxDays, this.cutoffHour, this.closedSunday);
        var span = formatRange(from, to);
        if (res.express) {
          // Badge on its own line: the two together overflow the PDP column.
          this.render('<span class="gm-edd__date gm-edd__date--express">⚡ '
            + esc(this.expressMessage) + '</span>'
            + '<span class="gm-edd__when">Delivery by <strong>' + span
            + '</strong></span>' + change + meta);
        } else {
          // Delhi: already fast, so no express badge — just the window.
          this.render('Estimated delivery by <span class="gm-edd__date">'
            + span + '</span>' + change + meta);
        }
      } else if (res.kind === 'date') {
        var when = formatDate(
          deliveryDate(res.days, this.cutoffHour, this.closedSunday));
        this.render('Estimated delivery by <span class="gm-edd__date">'
          + when + '</span>' + change + meta);
      } else if (res.kind === 'unserviceable') {
        this.render('<span class="gm-edd__result--warn">We don\'t deliver to '
          + esc(pin) + ' yet.</span>' + change);
      } else {
        // Not a pincode we serve. Never guess a date from the prefix.
        this.render('<span class="gm-edd__result--warn">Invalid pincode.</span>'
          + change);
      }
      this.bindChange();
    }

    bindChange() {
      var self = this;
      var btn = this.querySelector('[data-gm-edd-change]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        self.render('');
        self.input.value = '';
        self.input.focus();
      });
    }
  }

  if (!window.customElements.get('delivery-estimate')) {
    window.customElements.define('delivery-estimate', DeliveryEstimate);
  }
})();
